import React, { useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router';

import styled from '@emotion/styled';

import { Box, COLORS, Text } from '@clutter/clean';

import { Spacer } from '@shared/components/helpers';
import { useDebounce } from '@shared/hooks';
import { useLastDefined } from '@shared/hooks/state';

import { Notices } from '@portal/components/app/notices';
import { AccountConditionNotesBanner } from '@portal/components/items/condition_notes/account_condition_notes_banner';
import { BookNewAppointment } from '@portal/components/new_appointment';
import { NAV_BAR_HEIGHT } from '@portal/components/shared/layout';
import { IMPERSONATOR } from '@portal/config/impersonator';
import { appointmentURL, DEFAULT_ITEMS_STATUS_SLUG, ItemStatusSlug, itemsURL } from '@portal/config/routes';
import unsafeToReturnErrorIcon from '@portal/images/icons/unsafe_to_return_error.png';
import {
  useInventoryQuery,
  ItemStatus,
  Account__State,
  useInventoryContentQuery,
  useUsageWithDownsizeQuery,
  OrderStatusEnum,
  OrderTypeEnum,
} from '@portal/schema';

import { Detail } from '../detail/detail';
import { ItemViewContextProvider, useItemViewContext } from '../item_view_context';
import { Actions } from './actions';
import { Banner } from './banner';
import { Filters } from './filters';
import { Grid } from './grid';
import { NeedSignatureAlert } from './need_signature_alert';

const STATUSES: Record<ItemStatusSlug, ItemStatus> = {
  available: ItemStatus.Available,
  requested: ItemStatus.Requested,
  returned: ItemStatus.Returned,
  delayed: ItemStatus.Delayed,
  disposed: ItemStatus.Disposed,
};

export const PAGE_SIZE = 48;

const FullExpandContainer = styled.div`
  display: flex;
  position: absolute;
  left: 0;
  width: 100%;
  top: ${NAV_BAR_HEIGHT}px;
  height: calc(100% - ${NAV_BAR_HEIGHT}px);
  z-index: 0;
`;

const ClearItemSelection: React.FC<{ when: boolean }> = ({ when }) => {
  const { selectedItem, setSelectedItem } = useItemViewContext();
  useEffect(() => {
    if (when && selectedItem) setSelectedItem(undefined);
  }, [when, selectedItem, setSelectedItem]);
  return null;
};

const Content: React.FC = ({ children }) => {
  const { selectedItem } = useItemViewContext();
  return (
    <Box
      width="100%"
      display={[selectedItem ? 'none' : 'block', null, null, 'block']}
      maxWidth={['720px', null, null, '1376px']}
      margin="0 auto 8px"
      padding={['24px 24px 0', null, null, selectedItem ? '24px 116px 0' : null]}
    >
      {children}
    </Box>
  );
};

const Skeleton = () => (
  <ItemViewContextProvider>
    <FullExpandContainer>
      <Content>
        {IMPERSONATOR && <Spacer height="36px" />}
        <Banner.Loader />
        <Actions.Loader />
        <Filters.Loader />
        <Grid.Loader />
      </Content>
    </FullExpandContainer>
  </ItemViewContextProvider>
);

export const List: React.FC = () => {
  const searchParams = new URLSearchParams(location.search);
  const [query, setQuery] = useState(() => searchParams.get('query') || '');

  const { status, page } = useParams<{
    status: ItemStatusSlug;
    page?: string;
  }>();
  const debouncedQuery = useDebounce(query);
  const { data } = useInventoryQuery();
  useUsageWithDownsizeQuery(); // Pre-load usage bar while inventory loads

  const hasReturn = data?.orders?.some(
    (order) =>
      order.status !== OrderStatusEnum.Canceled &&
      (order.type === OrderTypeEnum.Return || order.type === OrderTypeEnum.PickupReturn),
  );
  const statusSlug = Object.values(ItemStatusSlug).includes(status as ItemStatusSlug)
    ? (status as ItemStatusSlug)
    : DEFAULT_ITEMS_STATUS_SLUG;
  const isDisposedTab = statusSlug === ItemStatusSlug.Disposed;
  const listPage = Math.max(1, page ? Number(page) : 1);

  const variables = (newPage = 1) => ({
    filters: {
      query: debouncedQuery ?? undefined,
      status: STATUSES[statusSlug],
    },
    query: debouncedQuery ?? undefined,
    page: Math.max(1, newPage),
    per: PAGE_SIZE,
    includeDisposedCount: Boolean(hasReturn),
  });
  const { data: customerItemData, fetchMore } = useInventoryContentQuery({
    variables: variables(listPage),
  });

  // Keep the last counts while a refetch is in flight. Do not reuse another tab's cards:
  // the disposed tab is read-only, so stale stored items would look disposed.
  const cachedCustomerItemData = useLastDefined(customerItemData);
  const [gridSnapshot, setGridSnapshot] = useState<{
    status: ItemStatusSlug;
    items: NonNullable<typeof customerItemData>['paginatedCustomerItems'];
  } | null>(null);

  useEffect(() => {
    if (!customerItemData?.paginatedCustomerItems) return;
    setGridSnapshot((current) => {
      if (current?.items === customerItemData.paginatedCustomerItems) return current;
      return { status: statusSlug, items: customerItemData.paginatedCustomerItems };
    });
  }, [customerItemData, statusSlug]);

  const gridItems = gridSnapshot?.status === statusSlug ? gridSnapshot.items : undefined;

  if (!data || !cachedCustomerItemData) return <Skeleton />;

  if (status !== statusSlug || (isDisposedTab && !hasReturn)) {
    return <Redirect to={itemsURL(DEFAULT_ITEMS_STATUS_SLUG)} />;
  }

  const needSignature = data?.needSignature;

  const account = data?.account;

  const pending = account?.onboardable && account.state === Account__State.Pending;
  const canceled = account?.onboardable && account.state === Account__State.Canceled;
  const rescheduleOffer = account.rescheduleOfferDetails;

  const unscheduledAuctionWins = data.account?.unscheduledStorageTreasuresAuctionsWon ?? [];
  const hasUnscheduledAuctionWin = unscheduledAuctionWins.length > 0;

  if (hasUnscheduledAuctionWin) {
    return <Redirect to={{ pathname: appointmentURL() }} />;
  }

  if (account && pending) {
    // A storage customer in a reonboardable state may have documents that need signing.
    // However, the NeedSignatureAlert component is here primarily to cover moving-only customers
    return (
      <>
        {needSignature && <NeedSignatureAlert needSignature={needSignature} />}
        <BookNewAppointment requestableItemsExist={account.requestableItemsExist} />
      </>
    );
  }

  return (
    <ItemViewContextProvider>
      <ClearItemSelection when={isDisposedTab} />
      <FullExpandContainer>
        <Content>
          {IMPERSONATOR && <Spacer height="36px" />}
          <Notices />
          {needSignature && <NeedSignatureAlert needSignature={needSignature} />}
          <AccountConditionNotesBanner count={account.itemsWithUnacknowledgedConditionNotesCount} />
          <Banner token={rescheduleOffer?.token} rescheduleOfferCoupon={rescheduleOffer?.coupon} />
          {(!canceled || account.requestableItemsExist) && <Actions />}
          <Filters
            initialQuery={query}
            onQueryChange={setQuery}
            orders={data?.orders}
            inventory={cachedCustomerItemData}
            itemsWithConditionNotesCount={data?.account?.itemsWithConditionNotesCount}
          />
          {isDisposedTab && (
            <Box.Flex alignItems="flex-start" gap="8px" margin="0 0 24px">
              <img src={unsafeToReturnErrorIcon} alt="" width={22} height={22} aria-hidden />
              <Text.Body color={COLORS.panther}>
                These are the items that have been disposed or items that were deemed{' '}
                <Text.Body as="span" color={COLORS.tealPrimary}>
                  unsafe for return
                </Text.Body>
                .
              </Text.Body>
            </Box.Flex>
          )}
          {gridItems ? (
            <Grid
              pageSize={PAGE_SIZE}
              readOnly={isDisposedTab}
              paginationData={gridItems}
              fetchMore={(nextPage) => fetchMore({ variables: variables(nextPage) })}
            />
          ) : (
            <Grid.Loader />
          )}
        </Content>
        {!isDisposedTab && <Detail />}
      </FullExpandContainer>
    </ItemViewContextProvider>
  );
};
